"use client";

import { useEffect, useState } from "react";

/** Breakpoints aligned with gallery grid: mobile 2x3, tablet 4x2, desktop 5x2, large 6x2 */
const BREAKPOINTS = {
  tablet: 768,
  desktop: 1280,
  large: 1536,
} as const;

function getPageSize(width: number): number {
  if (width >= BREAKPOINTS.large) return 12; // 6x2
  if (width >= BREAKPOINTS.desktop) return 10; // 5x2
  if (width >= BREAKPOINTS.tablet) return 8; // 4x2
  return 6; // 2x3
}

export function useGalleryPageSize(): number {
  const [pageSize, setPageSize] = useState(6);

  useEffect(() => {
    // rAF-throttle the resize handler. A raw `resize` listener reads
    // window.innerWidth on every event — many per second during a window drag —
    // and each read forces a synchronous layout (reflow), which is jank exactly
    // while the grid's images are decoding. Collapsing to one read per frame
    // removes that. (setPageSize is also a no-op when the discrete bucket is
    // unchanged, so it only re-renders the grid when a breakpoint is crossed.)
    let frame = 0;
    const update = () => setPageSize(getPageSize(window.innerWidth));
    const onResize = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        update();
      });
    };
    update();
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return pageSize;
}
